import React, {
  createContext, useCallback, useContext, useMemo, useRef, useState,
} from "react";
import { useDispatch } from "react-redux";
import {
  uploadMedia, UploadResult, UploadStatus,
} from "../UploadFile/uploadMedia";
import { attoDeskApi } from "../../Api/attoDeskApi";

/**
 * Global upload queue. Any page can:
 *   const { enqueue } = useUploads();
 *   await enqueue(file);
 *
 * The <UploadsPanel /> rendered by <UploadProvider /> shows progress
 * and lets the user cancel or dismiss completed/failed entries.
 */

export interface UploadTask {
  id: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  progress: number;          // 0–100 bytes-sent percentage
  status: UploadStatus;
  error?: string;
  result?: UploadResult;
  startedAt: number;
  controller: AbortController;
}

interface UploadContextValue {
  uploads: UploadTask[];
  enqueue: (file: File, companyId?: number) => Promise<UploadResult>;
  cancel: (id: string) => void;
  dismiss: (id: string) => void;
  clearFinished: () => void;
  hasActive: boolean;
}

const noop = () => Promise.reject(new Error("UploadProvider missing"));
const UploadContext = createContext<UploadContextValue>({
  uploads: [],
  enqueue: noop as any,
  cancel: () => {},
  dismiss: () => {},
  clearFinished: () => {},
  hasActive: false,
});

let _nextId = 0;
const nextId = () => `${Date.now()}-${++_nextId}`;

export const UploadProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [uploads, setUploads] = useState<UploadTask[]>([]);
  const dispatch = useDispatch();
  // Keep a ref so concurrent enqueues see the latest list when patching.
  const uploadsRef = useRef<UploadTask[]>([]);
  uploadsRef.current = uploads;

  const patch = useCallback((id: string, change: Partial<UploadTask>) => {
    setUploads(list =>
      list.map(u => (u.id === id ? { ...u, ...change } : u))
    );
  }, []);

  const enqueue = useCallback((file: File, companyId?: number): Promise<UploadResult> => {
    const id = nextId();
    const controller = new AbortController();
    const task: UploadTask = {
      id,
      fileName: file.name,
      fileSize: file.size,
      mimeType: file.type,
      progress: 0,
      status: "queued",
      startedAt: Date.now(),
      controller,
    };
    setUploads(list => [...list, task]);

    return uploadMedia(file, {
      onProgress: (p) => patch(id, { progress: p }),
      onStatus: (s) => patch(id, { status: s }),
      signal: controller.signal,
      companyId,
    })
      .then(result => {
        patch(id, { status: "done", progress: 100, result });
        // The upload goes through a raw XHR (not an RTK mutation), so nothing
        // would otherwise refresh cached media lists. Invalidate the "media" tag
        // GLOBALLY so every getMedia subscriber (Media Library, Playlist editor,
        // Template designer previews) refetches and the new file resolves — no
        // manual refresh, no "Media #N (not found in library)".
        dispatch(attoDeskApi.util.invalidateTags(["media"]));
        return result;
      })
      .catch(err => {
        const isAbort = err?.name === "AbortError";
        patch(id, {
          status: isAbort ? "cancelled" : "error",
          error: isAbort ? "Cancelled" : (err?.message ?? "Upload failed"),
        });
        throw err;
      });
  }, [patch, dispatch]);

  const cancel = useCallback((id: string) => {
    const task = uploadsRef.current.find(u => u.id === id);
    if (task && (task.status === "uploading" || task.status === "queued" || task.status === "processing")) {
      task.controller.abort();
    }
  }, []);

  const dismiss = useCallback((id: string) => {
    setUploads(list => list.filter(u => u.id !== id));
  }, []);

  const clearFinished = useCallback(() => {
    setUploads(list =>
      list.filter(u => u.status === "uploading" || u.status === "queued" || u.status === "processing")
    );
  }, []);

  const hasActive = uploads.some(
    u => u.status === "uploading" || u.status === "queued" || u.status === "processing"
  );

  const value = useMemo<UploadContextValue>(
    () => ({ uploads, enqueue, cancel, dismiss, clearFinished, hasActive }),
    [uploads, enqueue, cancel, dismiss, clearFinished, hasActive]
  );

  return (
    <UploadContext.Provider value={value}>{children}</UploadContext.Provider>
  );
};

export const useUploads = (): UploadContextValue => useContext(UploadContext);
